import type { ReactElement, ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import cx from 'clsx';
import { useIntl } from 'react-intl';
import { CloseIcon } from '../../assets/icons';
import { Button } from '../button';
import styles from './top-banner.module.css';
import { IconInfoRoundedOutlined } from './icons/icon-info-rounded-outlined';
import { IconWarningSignOutlined } from './icons/icon-warning-sign-outlined';

interface TopBannerProps {
  type?: 'info' | 'warning' | 'error';
  onClose: () => void;
  children: ReactNode;
  autoFocus?: boolean;
  className?: string;
  message?: string;
  dismissable?: boolean;
  closeButtonAriaLabel?: string;
}

export function TopBanner({
  type = 'info',
  onClose,
  children,
  autoFocus = true,
  className,
  message,
  dismissable = true,
  closeButtonAriaLabel = 'Close',
  ...props
}: TopBannerProps): ReactElement {
  const { formatMessage } = useIntl();
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoFocus && bannerRef.current) {
      bannerRef.current.focus();
    }
  }, [autoFocus]);

  const iconMap: Record<string, ReactNode> = {
    info: <IconInfoRoundedOutlined />,
    warning: <IconInfoRoundedOutlined />,
    error: <IconWarningSignOutlined />,
  };

  const icon = iconMap[type];

  const isString = typeof message === 'string';

  return (
    <div
      aria-label={closeButtonAriaLabel}
      className={cx(styles.topbar, styles[type], className)}
      data-test-topbanner
      data-test-topbanner-type={type}
      ref={bannerRef}
      role="banner"
      {...props}
    >
      <div className={styles.wrapper}>
        <span className={styles.icon} data-test-topbanner-icon={type}>
          {icon}
        </span>
        {isString ? (
          <p
            className={cx(styles.message, 'body-standard')}
            dangerouslySetInnerHTML={{ __html: String(message) }}
            data-test-topbanner-message
            id="top-banner-message"
          />
        ) : (
          <p
            className={cx(styles.message, 'body-standard')}
            data-test-topbanner-message
            id="top-banner-message"
          >
            {message}
          </p>
        )}
        {children}
      </div>
      {dismissable ? (
        <Button
          aria-label={formatMessage({ id: 'a11y-buttons-close-aria-label' })}
          className={styles.close}
          data-test-topbanner-close-button
          iconOnly
          onPress={onClose}
          type="button"
          variant="tertiary"
        >
          <CloseIcon aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}
